// ─────────────────────────────────────────────────────────────────────────────
// Eligibles query helpers — the single source of truth for turning the invoicing
// MONTH + the table-B display FILTERS into the /eligibles & /export query string
// (and back). Shared by the Éligibles screen (builds the "Exporter" link) and the
// Export screen (reads the carried month/filters and forwards them to the API), so
// the exported file honours exactly what was filtered on Éligibles.
// ─────────────────────────────────────────────────────────────────────────────

import type { EligiblesFilters } from '@mdu-invoicing/shared';

/** The current month as 'YYYY-MM' (browser-local; the user can pick another). */
export function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/** First/last calendar day of a 'YYYY-MM' month → the `/eligibles` from/to range.
 *  (`new Date(y, m, 0)` with a 1-based `m` is day 0 of the next month = last day of m.) */
export function monthBounds(month: string): { from: string; to: string } {
  const [y, m] = month.split('-').map(Number);
  const lastDay = new Date(y, m, 0).getDate();
  return { from: `${month}-01`, to: `${month}-${String(lastDay).padStart(2, '0')}` };
}

/** The filter keys carried in the URL (the EligiblesFilters string fields). */
const FILTER_KEYS = [
  'contractor',
  'area',
  'ssvDone',
  'tsaDone',
  'ssvValidation',
  'tsaValidation',
  'ssvDate',
  'tsaDate',
] as const;

/** Serialize month + filters → a query string for /eligibles & /export (omits empties). */
export function eligiblesQueryString(month: string, filters: EligiblesFilters): string {
  const { from, to } = monthBounds(month);
  const params = new URLSearchParams({ from, to });
  for (const k of FILTER_KEYS) {
    const v = filters[k];
    if (typeof v === 'string' && v.trim() !== '') params.set(k, v.trim());
  }
  return params.toString();
}

/** Parse a URLSearchParams back into { month, filters } (for the Export screen). */
export function parseEligiblesQuery(sp: URLSearchParams): { month: string; filters: EligiblesFilters } {
  const from = sp.get('from');
  const month = from && /^\d{4}-\d{2}/.test(from) ? from.slice(0, 7) : currentMonth();
  const filters: EligiblesFilters = {};
  for (const k of FILTER_KEYS) {
    const v = sp.get(k);
    if (v && v.trim() !== '') filters[k] = v.trim();
  }
  return { month, filters };
}
